import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, AlertTriangle, RefreshCw, Search, Download, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';

const CLASSIF = {
  candidato: { bg:'#FEE2E2', color:'#DC2626', label:'Candidato Descarte' },
  atencao:   { bg:'#FEF3C7', color:'#D97706', label:'Atenção' },
  normal:    { bg:'#DCFCE7', color:'#16A34A', label:'Normal' },
};

export default function Descartes() {
  const [animais, setAnimais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('candidato,atencao'); // padrão: ocultar normal
  const [busca, setBusca] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [historicos, setHistoricos] = useState({});
  const [avaliando, setAvaliando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setAnimais([]);
      const { data } = await api.get('/descartes');
      setAnimais(data);
    } catch { toast.error('Erro ao carregar descartes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const reavaliar = async () => {
    setAvaliando(true);
    try {
      setAnimais([]);
      await api.post('/descartes/avaliar');
      toast.success('Reavaliação concluída!');
      await carregar();
    } catch { toast.error('Erro ao reavaliar'); }
    finally { setAvaliando(false); }
  };

  const salvar = async (animal) => {
    try {
      await api.post('/descartes', { animal_id: animal.animal_id || animal.id, classificacao: animal.classificacao, pontuacao: animal.pontuacao, motivos: animal.motivos });
      toast.success(`Avaliação salva para ${animal.brinco}`);
    } catch { toast.error('Erro ao salvar'); }
  };

  const updateField = (id, campo, valor) => {
    setAnimais(prev => prev.map(a => a.id !== id ? a : { ...a, [campo]: valor }));
  };

  const carregarHistorico = async (animalId) => {
    if (historicos[animalId]) return;
    try {
      const { data } = await api.get(`/descartes/historico-reprodutivo/${animalId}`);
      setHistoricos(prev => ({ ...prev, [animalId]: data }));
    } catch { /* silencioso */ }
  };

  const toggleExpand = (animal) => {
    const isOpen = expandedId === animal.id;
    setExpandedId(isOpen ? null : animal.id);
    if (!isOpen) carregarHistorico(animal.animal_id || animal.id);
  };

  const exportarCSV = () => {
    if (filtrados.length === 0) return toast.error('Nenhum dado para exportar');
    const rows = [['Brinco','Nome','Classificação','Pontuação','Motivos']];
    filtrados.forEach(a => rows.push([
      a.brinco||'', a.animal_nome||'',
      CLASSIF[a.classificacao]?.label || a.classificacao,
      a.pontuacao||0,
      Array.isArray(a.motivos) ? a.motivos.join('; ') : (a.motivos||'')
    ]));
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿'+csv], { type:'text/csv;charset=utf-8;' }));
    const a = document.createElement('a'); a.href=url; a.download=`descartes_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado!');
  };

  const filtros = filtro.split(',').filter(Boolean);
  const filtrados = animais.filter(a => {
    if (filtros.length && !filtros.includes(a.classificacao)) return false;
    if (busca) {
      const t = busca.toLowerCase();
      if (!(a.brinco||'').toLowerCase().includes(t) && !(a.animal_nome||'').toLowerCase().includes(t)) return false;
    }
    return true;
  });

  const cands  = animais.filter(a => a.classificacao === 'candidato').length;
  const atenc  = animais.filter(a => a.classificacao === 'atencao').length;
  const norm   = animais.filter(a => a.classificacao === 'normal').length;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Descarte Técnico</h1><p>{cands} candidatos · {atenc} atenção · {norm} normais</p></div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={exportarCSV}><Download size={16}/> CSV</button>
          <button className="btn btn-primary" onClick={reavaliar} disabled={avaliando}>
            <RefreshCw size={16} style={{ animation: avaliando ? 'spin 1s linear infinite' : undefined }}/>
            {avaliando ? 'Reavaliando...' : 'Reavaliar Todos'}
          </button>
        </div>
      </div>

      {/* Critérios de Descarte */}
      <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'10px 16px', marginBottom:16, fontSize:13 }}>
        <strong style={{ color:'#92400E' }}>Critérios automáticos de avaliação:</strong>
        <span style={{ color:'#78350F', marginLeft:8 }}>
          GMD abaixo do esperado · Doenças recorrentes · Falha reprodutiva · Intervalo entre partos &gt; 365 dias · Sem prenhez há mais de 6 meses
        </span>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:16 }}>
        {[['Candidatos Descarte',cands,'#DC2626','candidato'],['Atenção',atenc,'#D97706','atencao'],['Normais',norm,'#16A34A','normal']].map(([label,val,cor,key]) => (
          <div key={key} className="stat-card" style={{ borderLeft:`4px solid ${cor}`, cursor:'pointer', opacity: filtros.includes(key) ? 1 : 0.5 }}
            onClick={() => setFiltro(f => {
              const arr = f.split(',').filter(Boolean);
              return arr.includes(key) ? arr.filter(x=>x!==key).join(',') : [...arr,key].join(',');
            })}>
            <div className="stat-label">{label}</div>
            <div className="stat-value" style={{ color:cor }}>{val}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
          <div className="search-box" style={{ flex:2, minWidth:200 }}>
            <Search size={16}/>
            <input placeholder="Buscar por brinco ou nome..." value={busca} onChange={e => setBusca(e.target.value)}/>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <span style={{ fontSize:13, color:'var(--text-secondary)', alignSelf:'center' }}>Exibir:</span>
            {Object.entries(CLASSIF).map(([key,c]) => (
              <button key={key} onClick={() => setFiltro(f => {
                const arr = f.split(',').filter(Boolean);
                return arr.includes(key) ? arr.filter(x=>x!==key).join(',') : [...arr,key].join(',');
              })}
                style={{ padding:'4px 12px', borderRadius:20, fontSize:12, border:`1px solid ${c.color}`,
                  background: filtros.includes(key) ? c.bg : '#fff',
                  color: filtros.includes(key) ? c.color : '#9CA3AF',
                  cursor:'pointer', fontWeight: filtros.includes(key) ? 600 : 400 }}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-secondary)' }}>Carregando avaliações...</div>
      ) : filtrados.length === 0 ? (
        <div className="empty-state">
          <AlertTriangle size={48}/>
          <h3>Nenhum animal encontrado</h3>
          <p>Clique em "Reavaliar Todos" para gerar avaliações, ou ajuste os filtros.</p>
        </div>
      ) : filtrados.map(animal => {
        const c = CLASSIF[animal.classificacao] || { bg:'#F3F4F6', color:'#6B7280', label:animal.classificacao };
        const isOpen = expandedId === animal.id;
        const hist   = historicos[animal.animal_id || animal.id];
        const motivos = (() => { try { return Array.isArray(animal.motivos) ? animal.motivos : JSON.parse(animal.motivos||'[]'); } catch { return []; } })();

        return (
          <div key={animal.id} className="card" style={{ marginBottom:12, border:`1px solid ${c.color}33`, padding:0, overflow:'hidden' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', cursor:'pointer' }}
              onClick={() => toggleExpand(animal)}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div>
                  <strong style={{ color:'var(--color-primary)', fontSize:15 }}>{animal.brinco}</strong>
                  {animal.animal_nome && <span style={{ color:'var(--text-secondary)', marginLeft:6, fontSize:14 }}>{animal.animal_nome}</span>}
                  {animal.lote_nome  && <span style={{ color:'var(--text-secondary)', marginLeft:6, fontSize:13 }}>· {animal.lote_nome}</span>}
                </div>
                <span style={{ background:c.bg, color:c.color, padding:'2px 10px', borderRadius:12, fontSize:12, fontWeight:600 }}>{c.label}</span>
                {motivos.length > 0 && motivos.slice(0,2).map((m,i) => (
                  <span key={i} style={{ background:'#F3F4F6', color:'#374151', padding:'2px 8px', borderRadius:10, fontSize:11 }}>{m}</span>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontWeight:700, fontSize:18, color:c.color }}>{animal.pontuacao||0} pts</span>
                {isOpen ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
              </div>
            </div>

            {isOpen && (
              <div style={{ borderTop:'1px solid #E5E7EB', padding:'14px 16px', background:'#FAFAFA' }}>

                {/* Histórico Reprodutivo */}
                {hist && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'#374151', marginBottom:8 }}>Histórico Reprodutivo</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10 }}>
                      {[
                        ['Coberturas', hist.coberturas, '#3B82F6'],
                        ['Prenhezes',  hist.prenhezes,  '#8B5CF6'],
                        ['Partos',     hist.partos,     '#16A34A'],
                        ['Cordeiros',  hist.cordeiros_nascidos, '#059669'],
                        ['Intervenções', hist.intervencoes, '#D97706'],
                      ].map(([label,val,cor]) => (
                        <div key={label} style={{ textAlign:'center', background:'#fff', border:`1px solid ${cor}33`, borderRadius:8, padding:'8px 4px' }}>
                          <div style={{ fontSize:20, fontWeight:700, color:cor }}>{val||0}</div>
                          <div style={{ fontSize:11, color:'#6B7280' }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    {hist.meses_sem_prenhe !== null && (
                      <div style={{ marginTop:10, padding:'8px 12px', background: hist.meses_sem_prenhe > 6 ? '#FEF2F2' : '#F0FDF4', borderRadius:6, fontSize:13, display:'flex', alignItems:'center', gap:6 }}>
                        <Clock size={14} style={{ color: hist.meses_sem_prenhe > 6 ? '#DC2626' : '#16A34A' }}/>
                        <span>Último parto: <strong>{hist.ultimo_parto ? formatDateBR(hist.ultimo_parto) : '—'}</strong></span>
                        {hist.meses_sem_prenhe !== null && (
                          <span style={{ marginLeft:12, color: hist.meses_sem_prenhe > 6 ? '#DC2626' : '#374151' }}>
                            {hist.meses_sem_prenhe} meses sem prenhez
                            {hist.meses_sem_prenhe > 6 && <strong> ⚠ acima do limite de 6 meses</strong>}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Edição da avaliação */}
                <div style={{ display:'flex', gap:16, flexWrap:'wrap', alignItems:'flex-end' }}>
                  <div className="form-group" style={{ margin:0, flex:1, minWidth:140 }}>
                    <label style={{ fontSize:13, fontWeight:600 }}>Classificação</label>
                    <select value={animal.classificacao||''} onChange={e => updateField(animal.id,'classificacao',e.target.value)}>
                      <option value="">Selecionar...</option>
                      <option value="candidato">Candidato Descarte</option>
                      <option value="atencao">Atenção</option>
                      <option value="normal">Normal</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin:0, width:120 }}>
                    <label style={{ fontSize:13, fontWeight:600 }}>Pontuação</label>
                    <input type="number" min="0" max="100" value={animal.pontuacao??''} onChange={e => updateField(animal.id,'pontuacao',e.target.value?Number(e.target.value):'')}/>
                  </div>
                  <button className="btn btn-primary" style={{ marginBottom:1 }} onClick={() => salvar(animal)}>Salvar Avaliação</button>
                </div>

                {/* Motivos completos */}
                {motivos.length > 0 && (
                  <div style={{ marginTop:12, fontSize:13 }}>
                    <strong>Motivos detectados automaticamente:</strong>
                    <ul style={{ margin:'4px 0 0', paddingLeft:20 }}>
                      {motivos.map((m,i) => <li key={i} style={{ color:'#6B7280' }}>{m}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
